/**
 * @fileoverview Unified comment navigation utilities for scrolling to different comment types
 * and handling various navigation scenarios across editor and renderer contexts.
 */
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';

import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';
import { expValEquals } from '@confluence/feature-experiments';

export enum ShortcutType {
	NEXT = 'next',
	PREVIOUS = 'previous',
	UNREAD = 'unread',
}
import {
	checkElementExpandInEditor,
	scrollToElementInEditor,
	openInlineCommentInEditor,
} from './editorAnnotations';
import {
	scrollToComment,
	watchForCommentContainerAndScroll,
	watchForCommentRepliesAndScroll,
	shouldScrollCommentThreadToTop,
	isCommentThreadReplyOutOfView,
	checkElementExpand,
	getNodeForTableORMacro,
} from './domUtils';

enum CommentType {
	INLINE = 'inline',
	GENERAL = 'general',
}

export enum ViewValues {
	OPEN = 'OPEN',
	UNREAD = 'UNREAD',
	DELETED_CONTENT = 'DELETED_CONTENT',
	RESOLVED = 'RESOLVED',
}

// Navigation types based on comment scrolling architecture
enum NavigationType {
	INLINE_ANNOTATION = 'inline_annotation',
	COMMENTS_PANEL = 'comments_panel',
	FOOTER_COMMENTS = 'footer_comments',
	DEEP_LINK = 'deep_link',
	KEYBOARD_SHORTCUT = 'keyboard_shortcut',
	INLINE_HEADER_ARROWS = 'inline_header_arrows',
}

// Scroll behavior options
export enum ScrollBehavior {
	INSTANT = 'instant',
	SMOOTH = 'smooth',
}

// Navigation context
export enum NavigationContext {
	EDITOR = 'editor',
	RENDERER = 'renderer',
	PANEL = 'panel',
	REPLY = 'reply',
}

// Options for navigation
export type NavigationOptions = {
	navigationType: NavigationType;
	scrollBehavior?: ScrollBehavior;
	context: NavigationContext;
	block?: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement?: HTMLElement | null;
	onScrollReady?: (element: Element) => void;
	onFallback?: () => void;
};

// Scroll logical position types
type ScrollLogicalPosition = 'start' | 'center' | 'end' | 'nearest';

/**
 * Handle inline annotation navigation (editor vs renderer)
 * Scrolls to and focuses on inline comment annotations in the content
 */
export const handleInlineAnnotationNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
	annotationId?: string;
}): boolean => {
	const { scrollBehavior, context, block, inline, markElement, annotationId } = options;

	if (!markElement) {
		return false;
	}

	// Handle different contexts
	switch (context) {
		case NavigationContext.EDITOR:
			// Check if element needs to be expanded in editor
			checkElementExpandInEditor(markElement);

			// Emit the setselectedannotation event
			const editorEmitter = getEditorAnnotationEventEmitter();
			editorEmitter.emit('setselectedannotation', markElement.id);

			// Scroll to the element in editor
			scrollToElementInEditor({
				targetElement: markElement,
				viewingRoomOffset: -100,
			});

			return true;

		case NavigationContext.RENDERER:
			// Renderer-specific logic with improved focus management
			const focusedMarkerRef = document.querySelector("[data-has-focus='true']");

			if (focusedMarkerRef?.id !== markElement.id) {
				const emitter = getRendererAnnotationEventEmitter();
				emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
					annotationId: markElement.id,
				});

				// Check if element needs to be expanded
				checkElementExpand(markElement, true);
			}

			// Scroll to the annotation mark
			markElement.scrollIntoView({
				behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
				block,
				inline,
			});
			return true;

		case NavigationContext.PANEL:
			// For panel context, ensure annotation is visible and highlighted
			if (annotationId) {
				// Emit events to ensure the annotation is properly focused
				const emitter = getRendererAnnotationEventEmitter();
				emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
					annotationId,
				});
			}

			// Scroll the annotation into view
			markElement.scrollIntoView({
				behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
				block,
				inline,
			});
			return true;

		default:
			return false;
	}
};

/**
 * Wait for the comments panel to be rendered in the DOM
 * Returns a Promise that resolves when the panel is available or times out after 5 seconds
 */
const waitForCommentsPanel = (): Promise<boolean> => {
	const commentsPanelListContainerId = 'comments-panel-list-container';

	return new Promise<boolean>((resolve) => {
		const observer = new MutationObserver(() => {
			const panel = document.querySelector(`[data-testid='${commentsPanelListContainerId}']`);
			if (panel) {
				observer.disconnect();
				resolve(true);
			}
		});

		// Start observing the document body for changes
		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		// Set a timeout to avoid infinite waiting
		setTimeout(() => {
			observer.disconnect();
			resolve(false);
		}, 5000); // 5 second timeout
	});
};

/**
 * Wait for the comments panel view/filter to change to the specified view
 * Returns a Promise that resolves when the view change is complete or times out after 3 seconds
 */
const waitForViewChange = (expectedView: string): Promise<boolean> => {
	return new Promise<boolean>((resolve) => {
		// Check if the view has already changed by checking the data-current-view attribute on the container
		const checkView = () => {
			const container = document.querySelector('[data-testid="comments-panel-list-container"]');
			if (container && container.getAttribute('data-current-view') === expectedView) {
				return true;
			}
		};

		if (checkView()) {
			resolve(true);
			return;
		}

		// Observe document body for both container creation and attribute changes
		// This handles cases where the container gets unmounted/remounted
		const observer = new MutationObserver(() => {
			if (checkView()) {
				observer.disconnect();
				resolve(true);
			}
		});

		// Observe the entire document body to catch:
		// 1. Container being created/removed
		// 2. Attribute changes on the container when it exists
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-current-view'],
		});

		// Set a timeout to avoid infinite waiting
		setTimeout(() => {
			observer.disconnect();
			resolve(false);
		}, 3000); // 3 second timeout
	});
};

/**
 * Check if an element is already fully visible within the panel
 * Avoids unnecessary scrolling if the element is already in view
 */
const isElementFullyVisible = (element: Element): boolean => {
	const elementRect = element.getBoundingClientRect();
	const panelContainer = document.querySelector('[data-testid="comments-panel-list-container"]');
	const panelRect = panelContainer?.getBoundingClientRect();

	if (!panelRect) return false;

	return elementRect.top >= panelRect.top && elementRect.bottom <= panelRect.bottom;
};

/**
 * Determine the optimal scroll position based on context and comment type
 * Considers whether to scroll to start, center, or end based on various factors
 */
const determineScrollPosition = (
	element: Element,
	block: ScrollLogicalPosition,
	scrollToEnd: boolean,
	commentType: CommentsType | undefined,
): ScrollLogicalPosition => {
	let scrollBlock: ScrollLogicalPosition = block;

	if (scrollToEnd) {
		// Check if the comment thread reply is out of view
		// Map CommentsType to CommentType enum for domUtils functions
		const commentTypeEnum =
			commentType === CommentsType.INLINE ? CommentType.INLINE : CommentType.GENERAL;
		const isOutOfView = isCommentThreadReplyOutOfView(element, commentTypeEnum);
		if (isOutOfView) {
			scrollBlock = 'end';
		}
	} else {
		// Check if we should scroll to top
		// Map CommentsType to CommentType enum for domUtils functions
		const commentTypeEnum =
			commentType === CommentsType.INLINE ? CommentType.INLINE : CommentType.GENERAL;
		const shouldScrollToTop = shouldScrollCommentThreadToTop(element, commentTypeEnum);
		if (shouldScrollToTop) {
			scrollBlock = 'start';
		}
	}

	return scrollBlock;
};

/**
 * Perform the actual scrolling operation with optimized behavior
 * Handles different scroll scenarios based on user interaction context
 */
const performScrollToElement = (
	element: Element,
	scrollBehavior: ScrollBehavior,
	block: ScrollLogicalPosition,
	scrollToEnd: boolean,
	hasKeyPressed: boolean,
	annotationClicked: boolean,
	scrollOnLoad: boolean,
	commentType: CommentsType | undefined,
): void => {
	// Skip scrolling if element is already fully visible
	if (isElementFullyVisible(element)) {
		return;
	}

	// Determine scroll behavior based on trigger type
	if (hasKeyPressed || annotationClicked || scrollOnLoad) {
		const scrollBlock = determineScrollPosition(element, block, scrollToEnd, commentType);

		element.scrollIntoView({
			behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
			block: scrollBlock,
		});
	} else {
		// Default scroll behavior
		element.scrollIntoView({
			behavior: scrollBehavior === ScrollBehavior.INSTANT ? 'instant' : 'smooth',
			block,
		});
	}
};

/**
 * Handle comments panel navigation (inline + footer threads)
 * Scrolls to comment threads within the comments panel container
 *
 * This function orchestrates the entire navigation process:
 * 1. Validates context and waits for panel if needed
 * 2. Handles special focus cases (reply containers, specific elements)
 * 3. Performs optimized scrolling based on user interaction context
 * 4. Watches for dynamic content if thread isn't immediately available
 */
export const handleCommentsPanelNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	commentId?: string; // The actual comment ID
	annotationId?: string; // Used for DOM selectors (comment-thread-{annotationId}-container)
	commentType?: CommentsType;
	scrollToEnd?: boolean;
	hasKeyPressed?: boolean;
	annotationClicked?: boolean;
	scrollOnLoad?: boolean;
}): boolean | Promise<boolean> => {
	const {
		scrollBehavior,
		context,
		block,
		commentId,
		annotationId,
		commentType,
		scrollToEnd = false,
		hasKeyPressed = false,
		annotationClicked = false,
		scrollOnLoad = false,
	} = options;
	// Only handle panel context navigation
	if (context !== NavigationContext.PANEL) {
		return false;
	}

	// Define panel container and thread selectors
	const commentsPanelListContainerId = 'comments-panel-list-container';
	// annotationId is used for DOM selectors, commentId is the actual comment identifier
	const commentThreadContainerId = `comment-thread-${annotationId}-container`;

	// Check if panel is available, wait if not
	const panelContainer = document.querySelector(`[data-testid='${commentsPanelListContainerId}']`);
	if (!panelContainer) {
		// Panel is not rendered yet, wait for it to appear
		return waitForCommentsPanel().then((panelAvailable) => {
			if (panelAvailable) {
				// Recursively call the function now that panel is available
				return handleCommentsPanelNavigation(options);
			}
			return false;
		});
	}

	// Look for the specific comment thread container
	const commentThreadElement = document.querySelector(
		`[data-testid='${commentThreadContainerId}']`,
	);

	// If comment thread exists, scroll immediately
	if (commentThreadElement) {
		performScrollToElement(
			commentThreadElement as Element,
			scrollBehavior,
			block,
			scrollToEnd,
			hasKeyPressed,
			annotationClicked,
			scrollOnLoad,
			commentType,
		);
		return true;
	}

	// Use optimized observer to watch for comment thread container creation
	commentId && watchForCommentContainerAndScroll(commentId);

	return true;
};

/**
 * Handle comments panel navigation with view change waiting
 * This function waits for the view to change to the specified view before performing navigation
 */
export const handleCommentsPanelNavigationWithViewChange = async (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	commentId?: string; // The actual comment ID
	annotationId: string; // Used for DOM selectors (comment-thread-{annotationId}-container)
	commentType?: CommentsType;
	scrollToEnd?: boolean;
	hasKeyPressed?: boolean;
	annotationClicked?: boolean;
	scrollOnLoad?: boolean;
	expectedView?: ViewValues; // The view we expect to be active (e.g., 'Open', 'Resolved')
	isReply?: boolean; // Whether this is a reply comment
}): Promise<boolean> => {
	const { expectedView, commentId, annotationId, ...navigationOptions } = options;

	// Only wait for view change for panel context
	if (navigationOptions.context === NavigationContext.PANEL && expectedView) {
		await waitForViewChange(expectedView);
	}

	// Then perform the navigation - pass both commentId and annotationId
	return handleCommentsPanelNavigation({
		...navigationOptions,
		commentId,
		annotationId,
	});
};

/**
 * WIP: Handle footer comments navigation (general page-level threads)
 */
export const handleFooterCommentsNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	block: ScrollLogicalPosition;
	inline: ScrollLogicalPosition;
	commentId: string;
	navigationContext?: NavigationContext;
}): boolean => {
	const { block, inline, commentId, scrollBehavior, navigationContext } = options;
	let containerId: string;
	if (!commentId) {
		return false;
	}

	// Check if comment already exists in DOM
	// Determine container ID based on navigation context:
	// - PANEL: uses comment-thread container
	// - REPLY: uses data-comment-id attribute selector
	// - Default (footer): uses footer-comment-thread container
	if (navigationContext === NavigationContext.PANEL) {
		containerId = `comment-thread-${commentId}-container`;
	} else if (navigationContext === NavigationContext.REPLY) {
		containerId = `[data-comment-id="${commentId}"]`;
	} else {
		containerId = `footer-comment-thread-${commentId}-container`;
	}

	// Use querySelector for REPLY context (attribute selector), getElementById for others (ID)
	const existingComment =
		navigationContext === NavigationContext.REPLY
			? document.querySelector(containerId)
			: document.getElementById(containerId);

	if (existingComment) {
		// Comment is already rendered, scroll to it immediately
		existingComment.scrollIntoView({
			behavior: scrollBehavior,
			block,
			inline,
		});
		return true;
	} else {
		// Comment not yet rendered, use DOM resolver to watch for it
		watchForCommentRepliesAndScroll({
			targetCommentId: commentId,
			onScrollReady: (commentElement) => {
				// Find the container element by ID or selector based on navigation context
				const containerElement =
					navigationContext === NavigationContext.REPLY
						? document.querySelector(containerId)
						: document.getElementById(containerId);
				const elementToScroll = containerElement || commentElement;
				elementToScroll.scrollIntoView({
					behavior: scrollBehavior,
					block,
					inline,
				});
			},
			containerId,
		});
		return true;
	}
};

/**
 * Handle deep link navigation (URL params)
 * Simple: scroll to comment once it's rendered on screen
 */
export const handleDeepLinkNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	context: NavigationContext;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	markElement: HTMLElement | null | undefined;
	commentId: string;
}): boolean => {
	const { scrollBehavior, block, inline, commentId, markElement, context } = options;

	if (!commentId || !markElement) {
		return false;
	}

	// Check if comment already exists in DOM
	const existingComment = document.querySelector(`[data-comment-id="${commentId}"]`);

	if (existingComment) {
		// Comment is already rendered, scroll to it immediately
		existingComment.scrollIntoView({
			behavior: scrollBehavior,
			block,
			inline,
		});
		return true;
	} else {
		// we need to click on the annotation in the editor if the comment is not yet rendered
		if (context === NavigationContext.EDITOR && markElement) {
			openInlineCommentInEditor({
				targetElement: markElement,
				targetElementID: markElement.id,
				shouldScroll: false,
			});
		}
		// Comment not yet rendered, use DOM resolver to watch for it
		watchForCommentRepliesAndScroll({
			targetCommentId: commentId,
			onScrollReady: (commentElement) => {
				commentElement.scrollIntoView({
					behavior: scrollBehavior,
					block,
					inline,
				});
			},
			markElement,
		});
		return true;
	}

	return false;
};

/**
 * Handle keyboard shortcut navigation (next/previous/unread)
 * Orchestrates navigation based on whether comments panel is open or not
 */
export const handleKeyboardShortcutNavigation = (options: {
	scrollBehavior: ScrollBehavior;
	block: ScrollLogicalPosition;
	inline?: ScrollLogicalPosition;
	commentId?: string;
	annotationId: string;
	commentType?: CommentsType;
	isCommentsPanelOpen?: boolean;
	hasKeyPressed?: boolean;
	scrollToEnd?: boolean;
	context?: NavigationContext;
	shortcutType?: ShortcutType;
	isResolved?: boolean;
}): boolean => {
	const {
		scrollBehavior,
		block,
		inline,
		annotationId,
		commentId,
		commentType,
		isCommentsPanelOpen,
		hasKeyPressed = true,
		scrollToEnd = false,
		context,
		shortcutType,
		isResolved,
	} = options;

	// Get the mark element for both inline navigation and annotation events
	let markElement = document.getElementById(annotationId);
	const commentElement = document.querySelector(`[data-comment-id="${commentId}"]`);

	// Check if the mark element is in a sticky table header and adjust if needed
	const isInStickyTableHeader =
		markElement?.closest && markElement.closest('.pm-table-sticky-wrapper');
	if (isInStickyTableHeader && markElement && expValEquals('confluence_comment_enable_table_header_navigation', 'isEnabled', true)) {
		// Get the real element from the non-sticky table using getNodeForTableORMacro
		markElement = getNodeForTableORMacro(markElement) || markElement;
	}

	if (commentType === CommentsType.FOOTER) {
		void handleFooterCommentsNavigation({
			scrollBehavior,
			block,
			inline: inline || 'center',
			commentId: annotationId,
		});
	}

	// If comments panel is open, use panel navigation
	if (isCommentsPanelOpen) {
		// For inline comments, also call handleInlineAnnotationNavigation to ensure proper inline comment behavior
		if (commentType === CommentsType.INLINE && !isResolved) {
			handleInlineAnnotationNavigation({
				scrollBehavior,
				context: context || NavigationContext.PANEL,
				block,
				inline: inline || 'center',
				markElement,
				annotationId,
			});
		}

		// This ensure the ViewComponent useEffect is not triggered
		if (markElement && !isResolved) {
			// Emit annotation events for panel navigation
			if (context === NavigationContext.EDITOR) {
				const editorEmitter = getEditorAnnotationEventEmitter();
				editorEmitter.emit('setselectedannotation', annotationId);
			} else if (context === NavigationContext.RENDERER) {
				const rendererEmitter = getRendererAnnotationEventEmitter();
				rendererEmitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
					annotationIds: [annotationId],
					eventTarget: null,
				});
			}
		}

		// Use the existing panel navigation logic
		const result = handleCommentsPanelNavigation({
			scrollBehavior,
			context: NavigationContext.PANEL,
			block,
			inline,
			annotationId,
			commentType,
			hasKeyPressed,
			scrollToEnd,
		});

		if (result instanceof Promise) {
			// For async panel navigation, return true immediately
			// The actual navigation will happen asynchronously
			return true;
		}

		return result;
	}

	if (!markElement) {
		return false;
	}

	// For unread navigation, scroll to the comment element with data-comment-id instead of markElement
	if (shortcutType === ShortcutType.UNREAD && commentElement) {
		commentElement.scrollIntoView({
			behavior: scrollBehavior,
			block,
			inline: inline || 'nearest',
		});
	} else {
		// For standalone navigation, we don't emit events - just scroll to the element
		markElement.scrollIntoView({
			behavior: scrollBehavior,
			block,
			inline: inline || 'nearest',
		});
	}
	return true;
};

/**
 * Handle inline header arrows navigation (UI navigation)
 * Uses scrollToComment once the new comment element is available
 */
export const handleInlineHeaderArrowsNavigation = (options: {
	previousMarkerRef: string; // Previous comment marker ref
	nextMarkerRef: string; // Next comment marker ref
	context: 'editor' | 'renderer' | 'panel';
}): boolean => {
	const { previousMarkerRef, nextMarkerRef, context } = options;

	// Get the next comment element
	const nextMarkElement = document.getElementById(nextMarkerRef);
	if (!nextMarkElement) {
		return false;
	}

	// Get the previous and next comment elements
	const previousCommentElement = document.getElementById(previousMarkerRef);
	const nextCommentElement = document.getElementById(nextMarkerRef);

	if (previousCommentElement && nextCommentElement) {
		if (context === NavigationContext.EDITOR) {
			// For editor: use scrollableContainer.scrollBy with difference offset
			const scrollableContainer = document.querySelector(
				'.fabric-editor-popup-scroll-parent',
			) as HTMLElement;
			if (scrollableContainer) {
				// Calculate difference between current and previous comment positions
				const currentRect = nextCommentElement.getBoundingClientRect();
				const previousRect = previousCommentElement.getBoundingClientRect();
				const differenceOffset = currentRect.top - previousRect.top;

				scrollableContainer.scrollBy(0, differenceOffset);
			}
		} else {
			// For renderer: use scrollToComment
			scrollToComment(nextCommentElement, previousCommentElement, true, false);
		}
	}

	return true;
};
